// See https://auspost.com.au/payments/docs/securepay/?javascript#securepay-api-card-payments-javascript-sdk

export declare class SecurePayUI {
  init(options: SecurePayUIConfig): SecurePayUI;

  tokenise(): void;
  reset(): void;
}

export interface SecurePayUIConfig {
  containerId: string; // HTML "id" attribute
  scriptId: string; // HTML "id" attribute
  clientId: string;
  merchantCode: string;

  card: SecurePayUiCardConfig;

  style?: SecurePayUiStyleConfig;
  mode?: SecurePayUIMode;

  // Should be present for in dcc mode. A JWT token from Order Details.
  checkoutInfo?: {
    orderToken: string;
  };

  onLoadComplete?(): void;
}

export interface SecurePayUiStyleConfig {
  backgroundColor: string;
  label: {
    font: SecurePayUiFontStyle;
  };
  input: {
    font: SecurePayUiFontStyle;
  };
}

export interface SecurePayUiFontStyle {
  family: string;
  size: string;
  color: string;
}

export interface SecurePayUiCardConfig {
  allowedCardTypes: SecurePayUICardType[];
  showCardIcons: boolean;

  /**
   * Invoked when card type changes.
   * Returns the card type ('visa', 'mastercard', 'amex' or 'diners' or 'unknown' if type cannot be determined).
   */
  onCardTypeChange?(cardType: SecurePayUICardType): void;

  /**
   * Invoked when BIN changes. Returns the card BIN (Bank Identification Number).
   */
  onBINChange?(cardBIN: number): void;

  /**
   * Invoked when card form validity state changes. Returns a boolean flag indicating form validity.
   */
  onFormValidityChange?(valid: boolean): void;

  /**
   * Could be invoked in dcc mode only.
   * Invoked, when quote retrieval of dynamic currency conversion is successful.
   * securePayUI.tokenise() should not be invoke prior receiving this callback, otherwise
   * securePayUI.tokenise() call result in DCC_QUOTE_IN_PROGRESS error provided in onTokeniseError callback.
   */
  onDCCQuoteSuccess?(quote: SecurePayUIDCCQuote): void;

  /**
   * Could be invoked in dcc mode only.
   * Invoked, when quote retrieval fails, for instance when orderToken is invalid.
   * securePayUI.tokenise() method should not be invoked until UI component is reinitialised with valid configuration details.
   */
  onDCCQuoteError?(errors: SecurePayUIError[]): void;

  /**
   * Invoked when card is successfully tokenised. Returns the SecurePayUITokenizedCard that was created.
   */
  onTokeniseSuccess?(tokenisedCard: SecurePayUITokenizedCard): void;

  /**
   * Invoked when card tokenization failed
   */
  onTokeniseError?(errors: SecurePayUIError[]): void;
}

export interface SecurePayUITokenizedCard {
  merchantCode: string;
  token: string; // A tokenised payment instrument reference. Use this token in make payment call.
  scheme: SecurePayUICardType; // A card scheme, e.g. visa, mastercard, diners, amex.
  bin: string; // Bank identification number.
  last4: string; // The last 4 digits of the card number. (Please note the number of digits returned may vary due to the card scheme, eg: 3 digits may be returned)
  expiryMonth: string; // Two digit number representing the card expiry month.
  expiryYear: string; // Two digit number representing the card expiry year.
  createdAt: string; // In ISO Date Time format with offset from UTC/Greenwich e.g. 2021-07-23T13:00:53.128+10:00.
  dccQuote?: SecurePayUIDCCQuote; // Dynamic currency conversion quote, will be present in dcc mode only.
}

export interface SecurePayUIDCCQuote {
  base: {
    currency: 'AUD';
  };
  converted: {
    currency: string;
    amount: number;
    exchangeRate: {
      value: number;
      markup: number;
    };
  };
}

export interface SecurePayUIError {
  id: string;
  code: number;
  detail: string;

  source?: {
    pointer: string;
  };
}

export enum SecurePayUIMode {
  DCC = 'dcc',
  CHECKOUT = 'checkout',
}

export enum SecurePayUICardType {
  VISA = 'visa',
  MASTERCARD = 'mastercard',
  AMEX = 'amex',
  DINERS = 'diners',

  UNKNOWN = 'unknown',
}
